import { type FC, useCallback, useState, useEffect } from 'react';
import {
  UiButton,
  UiModal,
  UiModalBody,
  UiModalCloseButton,
  UiModalContent,
  UiModalFooter,
  UiModalHeader,
  UiModalOverlay,
  UiStack,
  uiStyles,
  UiText,
  UiFormControl,
  UiFormLabel,
  UiFormHelperText,
  UiInput
} from '@/lib/ui';
import SimpleSelectElement, { type Option as SelectOption } from '@/base/Form/SimpleSelectElement';
import SimpleMultiSelectElement, { type Option } from '@/base/Form/SimpleMultiSelectElement';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTenantApi } from '@/account/hook/useTenantApi';
import { useGlobalToast } from '@/lib/hook';
import { useApiErrorHandler } from '@/account/hook/useApiErrorHandler';
import { registration as registrationAPI } from '@/api';
import { type ApiResponseSingle } from '@/api/tenantClient';
import { type CreateRegistrationOptionsResponse, type AttendeeCategoryOption, type DelegateTypeOption, type SessionsPricingOption } from '@/api/registration';
import { useEventOptionsQuery } from '@/registration/hook/useEventOptions';
import { type OnChangeValue } from 'chakra-react-select';

export interface CreateRegistrationModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

interface FormData {
  eventId: string
  email: string
  firstName: string
  lastName: string
  attendeeCategoryId: string
  delegateTypeId: string
  sessionsPricings: Option[]
}

const CreateRegistrationModal: FC<CreateRegistrationModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const { createTenantAdminApiRequest } = useTenantApi();
  const { showToast } = useGlobalToast();
  const { reportToGlobal } = useApiErrorHandler();
  const { eventOptions, isLoading: isLoadingEvents } = useEventOptionsQuery();

  const [formData, setFormData] = useState<FormData>({
    eventId: '',
    email: '',
    firstName: '',
    lastName: '',
    attendeeCategoryId: '',
    delegateTypeId: '',
    sessionsPricings: []
  });

  const [error, setError] = useState<string | null>(null);

  // Fetch registration types and sessions pricings based on selected event
  const { data: formOptions, isLoading: isLoadingOptions } = useQuery<ApiResponseSingle<CreateRegistrationOptionsResponse>>({
    queryKey: ['createRegistrationOptions', formData.eventId],
    queryFn: async () => {
      return await registrationAPI.getCreateRegistrationOptions(createTenantAdminApiRequest)({
        eventId: parseInt(formData.eventId)
      });
    },
    enabled: !!formData.eventId
  });

  // Reset dependent fields when event changes
  useEffect(() => {
    setFormData(prev => {
      return {
        ...prev,
        attendeeCategoryId: '',
        delegateTypeId: '',
        sessionsPricings: []
      };
    });
  }, [formData.eventId]);

  // Create registration mutation
  const { mutate: createRegistration, isLoading: isCreating } = useMutation({
    mutationFn: async () => {
      setError(null);
      const response = await registrationAPI.createManualRegistration(createTenantAdminApiRequest)({
        eventId: parseInt(formData.eventId),
        email: formData.email.toLowerCase(),
        firstName: formData.firstName,
        lastName: formData.lastName,
        attendeeCategoryId: parseInt(formData.attendeeCategoryId),
        delegateTypeId: parseInt(formData.delegateTypeId),
        sessionsPricingIds: formData.sessionsPricings.map(sp => { return sp.value as number; })
      });

      // Check for API errors in response
      if (response.errors && response.errors.length > 0) {
        throw new Error(response.errors[0]);
      }

      return response;
    },
    onSuccess: () => {
      showToast.success('Registration created successfully');
      resetForm();
      setError(null);
      onSuccess();
      onClose();
    },
    onError: (error: Error) => {
      const errorMessage = error.message || 'Failed to create registration';
      setError(errorMessage);
      showToast.error(errorMessage);
      reportToGlobal(error);
    }
  });

  const resetForm = () => {
    setFormData({
      eventId: '',
      email: '',
      firstName: '',
      lastName: '',
      attendeeCategoryId: '',
      delegateTypeId: '',
      sessionsPricings: []
    });
    setError(null);
  };

  const handleSubmit = useCallback(() => {
    setError(null);
    if (!formData.eventId || !formData.email || !formData.firstName || !formData.lastName || !formData.attendeeCategoryId || !formData.delegateTypeId) {
      const errorMsg = 'Please fill in all required fields';
      setError(errorMsg);
      showToast.error(errorMsg);
      return;
    }
    createRegistration();
  }, [formData, createRegistration, showToast]);

  const handleClose = useCallback(() => {
    resetForm();
    setError(null);
    onClose();
  }, [onClose]);

  const handleEventChange = useCallback((option: OnChangeValue<SelectOption, false>) => {
    setFormData(prev => {
      return {
        ...prev,
        eventId: option?.value?.toString() || ''
      };
    });
  }, []);

  const handleAttendeeCategoryChange = useCallback((option: OnChangeValue<SelectOption, false>) => {
    setFormData(prev => {
      return {
        ...prev,
        attendeeCategoryId: option?.value?.toString() || ''
      };
    });
  }, []);

  const handleDelegateTypeChange = useCallback((option: OnChangeValue<SelectOption, false>) => {
    setFormData(prev => {
      return {
        ...prev,
        delegateTypeId: option?.value?.toString() || ''
      };
    });
  }, []);

  const handleSessionsChange = useCallback((newValue: OnChangeValue<Option, boolean>) => {
    setFormData(prev => {
      return {
        ...prev,
        sessionsPricings: newValue as Option[] || []
      };
    });
  }, []);

  return (
    <UiModal
      onClose={handleClose}
      size={'xl'}
      closeOnEsc={true}
      motionPreset={'scale'}
      scrollBehavior={'inside'}
      isOpen={isOpen}
    >
      <UiModalOverlay />
      <UiModalContent borderRadius={uiStyles.borderRadius} p={2}>
        <UiModalHeader fontSize={'title'}>Create Registration</UiModalHeader>
        <UiModalCloseButton alignSelf={'flex-end'} p={4} size={'lg'} />
        <UiModalBody mt={{ base: 4, md: 0 }}>
          <UiStack spacing={4}>
            {error && (
              <UiText color={'red.500'} variant={'body2'}>{error}</UiText>
            )}

            <UiFormControl isRequired>
              <UiFormLabel>Event</UiFormLabel>
              <SimpleSelectElement
                optionValue={formData.eventId}
                onChange={handleEventChange}
                options={eventOptions}
                placeholder="Select an event"
                isLoading={isLoadingEvents}
              />
            </UiFormControl>

            <UiFormControl isRequired>
              <UiFormLabel>Email</UiFormLabel>
              <UiInput
                type="email"
                value={formData.email}
                onChange={(e) => { return setFormData({ ...formData, email: e.target.value }); }}
                placeholder="email@example.com"
              />
              <UiFormHelperText>
                If a user exists with this email, the registration will be attached to that user (otherwise a new user will be created). Any existing registrations for this user at this event will be automatically cancelled.
              </UiFormHelperText>
            </UiFormControl>

            <UiFormControl isRequired>
              <UiFormLabel>First Name</UiFormLabel>
              <UiInput
                value={formData.firstName}
                onChange={(e) => { return setFormData({ ...formData, firstName: e.target.value }); }}
                placeholder="First Name"
              />
            </UiFormControl>

            <UiFormControl isRequired>
              <UiFormLabel>Last Name</UiFormLabel>
              <UiInput
                value={formData.lastName}
                onChange={(e) => { return setFormData({ ...formData, lastName: e.target.value }); }}
                placeholder="Last Name"
              />
            </UiFormControl>

            <UiFormControl isRequired isDisabled={!formData.eventId}>
              <UiFormLabel>Attendee category</UiFormLabel>
              <SimpleSelectElement
                optionValue={formData.attendeeCategoryId}
                onChange={handleAttendeeCategoryChange}
                options={formOptions?.item?.attendeeCategories?.map((ac: AttendeeCategoryOption) => {
                  return {
                    value: ac.id,
                    label: ac.name
                  };
                }) || []}
                placeholder="Select attendee category"
                isLoading={isLoadingOptions}
              />
            </UiFormControl>

            <UiFormControl isRequired isDisabled={!formData.eventId}>
              <UiFormLabel>Registration type</UiFormLabel>
              <SimpleSelectElement
                optionValue={formData.delegateTypeId}
                onChange={handleDelegateTypeChange}
                options={formOptions?.item?.delegateTypes?.map((type: DelegateTypeOption) => {
                  return {
                    value: type.id,
                    label: type.name
                  };
                }) || []}
                placeholder="Select registration type"
                isLoading={isLoadingOptions}
              />
            </UiFormControl>

            <UiFormControl isDisabled={!formData.eventId}>
              <UiFormLabel>Sessions</UiFormLabel>
              <SimpleMultiSelectElement
                options={formOptions?.item?.sessionsPricings?.map((sp: SessionsPricingOption) => {
                  return {
                    value: sp.id,
                    label: sp.name
                  };
                }) || []}
                value={formData.sessionsPricings}
                onChange={handleSessionsChange}
                placeholder="Select sessions"
                optionValue="value"
                isLoading={isLoadingOptions}
              />
            </UiFormControl>
          </UiStack>
        </UiModalBody>
        <UiModalFooter>
          <UiButton variant="ghost" mr={3} onClick={handleClose}>
            Cancel
          </UiButton>
          <UiButton
            onClick={handleSubmit}
            isLoading={isCreating}
            isDisabled={isLoadingEvents || isLoadingOptions}
          >
            Create Registration
          </UiButton>
        </UiModalFooter>
      </UiModalContent>
    </UiModal>
  );
};

export default CreateRegistrationModal;
